import React, { useContext, useEffect, useMemo, useState } from "react";
import { AlertTitle, Box, Button, Divider, Paper, Typography } from "@mui/material";
import { Feature, LineString, Point } from "geojson";
import { omit } from "lodash";

import { LoadingButton } from "ui";

import { selectLayerByVersionAndLayerKey, useGetFeaturesQuery } from "fond/api/layersSlice";
import { DRAWING_PROPERTIES_TO_EXCLUDE } from "fond/constants";
import MapOverlay from "fond/map/MapOverlay";
import { MapContext } from "fond/map/MapProvider";
import { EditMode, setEditMode } from "fond/project/redux";
import { addImport } from "fond/redux/imports";
import { featureCollection } from "fond/turf";
import { LayerIds, MuPowerEditableLayerIds } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, ConfirmModal, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

import MuPowerUndergroundHandler from "./MuPowerUndergroundHandler";

import { Alert } from "../InputDataPanel.styles";

/**
 * Mapping from the internal draw layerId (used by UG draw modes/styles) to
 * the mu_power layer key used for API uploads.
 */
const drawLayerIdToMuPowerKey: Record<string, string> = {
  [LayerIds.inStreet]: MuPowerEditableLayerIds.ugPath,
  [LayerIds.inStreetPoints]: MuPowerEditableLayerIds.ugPoints,
};

const MuPowerDraw: React.FC = () => {
  const dispatch = useAppDispatch();
  const { drawControl, isDirty: isDrawingDirty, setDrawMode } = useContext(MapContext);
  const { goBack, clear } = useStackedNavigationContext();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const versionId = useAppSelector((state) => state.project.versionId);

  // Look up the API Layer IDs for the data_prep UG layers
  const ugPathLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerEditableLayerIds.ugPath }));
  const ugPointsLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerEditableLayerIds.ugPoints }));

  // Fetch features from the API
  const { data: pathFeatures, isLoading: pathLoading } = useGetFeaturesQuery({ layerId: ugPathLayer?.ID ?? "" }, { skip: !ugPathLayer?.ID });
  const { data: pointFeatures, isLoading: pointsLoading } = useGetFeaturesQuery({ layerId: ugPointsLayer?.ID ?? "" }, { skip: !ugPointsLayer?.ID });

  const isLoading = pathLoading || pointsLoading;

  // Set edit mode to prevent map click from opening properties panel
  useEffect(() => {
    dispatch(setEditMode(EditMode.edit));
    return () => {
      dispatch(setEditMode(EditMode.none));
    };
  }, [dispatch]);

  // Map API features to use the internal draw layerId for compatibility with UG draw modes/styles
  const initialFeatures: Feature<LineString | Point>[] = useMemo(() => {
    const paths = (pathFeatures ?? []).map((f) => ({
      ...f,
      properties: { ...f.properties, layerId: LayerIds.inStreet },
    })) as Feature<LineString | Point>[];

    const points = (pointFeatures ?? []).map((f) => ({
      ...f,
      properties: { ...f.properties, layerId: LayerIds.inStreetPoints },
    })) as Feature<LineString | Point>[];

    return [...paths, ...points];
  }, [pathFeatures, pointFeatures]);

  const handleBack = () => {
    if (isDrawingDirty) {
      setShowConfirm(true);
    } else {
      setDrawMode("no_feature");
      goBack();
    }
  };

  const handleConfirmDiscard = () => {
    setShowConfirm(false);
    setDrawMode("no_feature");
    goBack();
  };

  const handleFinish = async () => {
    if (!drawControl.current) return;
    setIsSaving(true);

    const allFeatures = drawControl.current.getAll().features;

    // Build a GeoJSON file per mu_power layer key
    for (const [drawLayerId, muPowerKey] of Object.entries(drawLayerIdToMuPowerKey)) {
      const layerFeatures = allFeatures
        .filter((f) => f.properties?.layerId === drawLayerId && f.geometry.coordinates.length >= 2)
        .map((f) => ({
          ...f,
          properties: omit(f.properties, DRAWING_PROPERTIES_TO_EXCLUDE),
        }));

      const fc = featureCollection(layerFeatures);
      const file = new File([JSON.stringify(fc)], `${muPowerKey}.geojson`, { type: "application/geo+json" });

      await dispatch(
        addImport({
          versionId,
          layerKey: muPowerKey,
          displayName: muPowerKey.split("/").pop() || muPowerKey,
          files: [file],
        })
      );
    }

    setDrawMode("no_feature");
    setIsSaving(false);
    clear();
  };

  if (isLoading) {
    return (
      <>
        <StackedNavigationHeader onBackButtonClick={handleBack} title="Edit Underground" />
        <Box display="flex" justifyContent="center" p={4}>
          <BlockSpinner spinnerProps={{ size: 30 }} />
        </Box>
      </>
    );
  }

  return (
    <>
      <StackedNavigationHeader onBackButtonClick={handleBack} title="Edit Underground" />
      <Box data-testid="mu-power-draw-panel" padding={1}>
        <Alert severity="info" data-testid="layer-alert">
          <AlertTitle sx={{ mb: 0 }}>Edit UG Paths &amp; Points</AlertTitle>
        </Alert>
        <Divider sx={{ mt: 3 }} />
        <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
          <Button color="primary" size="small" onClick={handleBack} disabled={isSaving}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            size="small"
            onClick={handleFinish}
            sx={{ ml: 1, px: 2 }}
            data-testid="finish-button"
            disabled={!isDrawingDirty}
            loading={isSaving}
          >
            Finish
          </LoadingButton>
        </Box>
      </Box>

      <MuPowerUndergroundHandler initialFeatures={initialFeatures} />

      <ConfirmModal
        open={showConfirm}
        content="Are you sure you want to discard your changes?"
        onConfirm={handleConfirmDiscard}
        onCancel={() => setShowConfirm(false)}
        data-testid="confirm-discard"
      />

      {isSaving && (
        <MapOverlay>
          <Paper sx={{ py: 1, px: 3, textAlign: "center", width: 200 }}>
            <BlockSpinner spinnerProps={{ size: 20 }} />
            <Typography component="p" variant="caption" pt={1}>
              Saving changes...
            </Typography>
          </Paper>
        </MapOverlay>
      )}
    </>
  );
};

export default MuPowerDraw;
